import React, { useMemo } from "react";
import moment from "moment";
import { DataTable } from "../../../ui-components/data-table";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import { useDataTableColumnPreferences } from "../../../hooks/useDataTableColumnPreferences";
import { PaymentSummary } from "../../../app/data/payment/PaymentSession";
import { PAYMENT_METHOD_OPTIONS, PAYMENT_PROVIDER_OPTIONS, PAYMENT_STATUS_OPTIONS } from "./paymentAdminUtils";
import getPaymentSearchColumns, {
  PAYMENT_SEARCH_COLUMN_OPTIONS,
  PAYMENT_SEARCH_COLUMNS_HIDDEN_BY_DEFAULT,
} from "./paymentSearchColumns";

const MAX_EXPORT_DAYS = 366;

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 10000 }),
};

export interface PaymentSearchSectionProps {
  accountNumber: string;
  invoiceNumber: string;
  status: string;
  paymentMethod: string;
  provider: string;
  dateFrom: string;
  dateTo: string;
  rows: PaymentSummary[];
  error: string | null;
  exportError: string | null;
  isLoading: boolean;
  isExporting: boolean;
  infiniteScrollHasNext: boolean;
  infiniteScrollLoading: boolean;
  onAccountChange: (value: string) => void;
  onInvoiceChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onPaymentMethodChange: (value: string) => void;
  onProviderChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onSearch: () => void;
  onExportCsv: () => void;
  onInfiniteScroll: () => void;
  onRowClick: (paymentId: string) => void;
}

const PaymentSearchSection: React.FC<PaymentSearchSectionProps> = ({
  accountNumber,
  invoiceNumber,
  status,
  paymentMethod,
  provider,
  dateFrom,
  dateTo,
  rows,
  error,
  exportError,
  isLoading,
  isExporting,
  infiniteScrollHasNext,
  infiniteScrollLoading,
  onAccountChange,
  onInvoiceChange,
  onStatusChange,
  onPaymentMethodChange,
  onProviderChange,
  onDateFromChange,
  onDateToChange,
  onSearch,
  onExportCsv,
  onInfiniteScroll,
  onRowClick,
}) => {
  const statusOption = PAYMENT_STATUS_OPTIONS.find((o) => o.value === status) ?? PAYMENT_STATUS_OPTIONS[0];
  const methodOption =
    PAYMENT_METHOD_OPTIONS.find((o) => o.value === paymentMethod) ?? PAYMENT_METHOD_OPTIONS[0];
  const providerOption =
    PAYMENT_PROVIDER_OPTIONS.find((o) => o.value === provider) ?? PAYMENT_PROVIDER_OPTIONS[0];
  const columnPrefs = useDataTableColumnPreferences({
    instanceKey: "payment-admin-search",
    columnOptions: PAYMENT_SEARCH_COLUMN_OPTIONS,
    hiddenByDefault: PAYMENT_SEARCH_COLUMNS_HIDDEN_BY_DEFAULT,
  });
  const columns = useMemo(() => getPaymentSearchColumns(), []);

  const dateRangeError = useMemo(() => {
    if (!dateFrom || !dateTo) return "Set a from and to date to export paid invoices.";
    const start = moment(dateFrom).startOf("day");
    const end = moment(dateTo).startOf("day");
    if (!start.isValid() || !end.isValid()) return "Set a from and to date to export paid invoices.";
    if (end.isBefore(start)) return "End date must be on or after the start date.";
    if (end.diff(start, "days") > MAX_EXPORT_DAYS) return "Date range cannot be longer than 366 days.";
    return null;
  }, [dateFrom, dateTo]);

  const exportDisabled = !dateFrom || !dateTo || !!dateRangeError || isExporting;

  return (
    <>
      <div className="xgs-payment-admin__filters">
        <LabeledTextInput
          label="Account"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          value={accountNumber}
          onChange={(e) => onAccountChange(e.target.value)}
          placeholder="Account #"
        />
        <LabeledTextInput
          label="Invoice"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          value={invoiceNumber}
          onChange={(e) => onInvoiceChange(e.target.value)}
          placeholder="Invoice #"
        />
        <LabeledSelectInput
          label="Status"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          options={PAYMENT_STATUS_OPTIONS}
          value={statusOption}
          onValueChange={(option) => onStatusChange(option?.value ?? "")}
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
        <LabeledSelectInput
          label="Payment type"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          options={PAYMENT_METHOD_OPTIONS}
          value={methodOption}
          onValueChange={(option) => onPaymentMethodChange(option?.value ?? "")}
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
        <LabeledSelectInput
          label="Provider"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          options={PAYMENT_PROVIDER_OPTIONS}
          value={providerOption}
          onValueChange={(option) => onProviderChange(option?.value ?? "")}
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
        <LabeledDateRangeInput
          label="Date"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input xgs-payment-admin__date-range"
          start={dateFrom}
          end={dateTo}
          onStartChange={onDateFromChange}
          onEndChange={onDateToChange}
        />
        <Button theme={ButtonThemes.blue} onClick={onSearch}>
          Search
        </Button>
        <span className="xgs-payment-admin__export-wrap" title={dateRangeError || undefined}>
          <Button
            theme={ButtonThemes.gray}
            onClick={onExportCsv}
            disabled={exportDisabled}
            spinner={isExporting}
            style={exportDisabled ? { pointerEvents: "none" } : undefined}
          >
            Export CSV
          </Button>
        </span>
      </div>
      {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
      {exportError && <XGSErrorMessage>{exportError}</XGSErrorMessage>}
      <DataTable
        isLoading={isLoading}
        keepTableOnLoading
        columns={columns}
        data={rows}
        rowHeight={52}
        cursorPointer
        onRowClicked={(row: PaymentSummary) => onRowClick(row.paymentId)}
        responsive
        sorting
        enableMultiSort
        enableRowSelection={false}
        {...columnPrefs}
        showColumnSelector
        columnSelectorOptions={PAYMENT_SEARCH_COLUMN_OPTIONS}
        enableColumnResize
        enableColumnReorder
        stickyHeaders
        noResultsText="No payments found"
        infiniteScroll
        infiniteScrollHasNext={infiniteScrollHasNext}
        infiniteScrollLoading={infiniteScrollLoading}
        onInfiniteScroll={onInfiniteScroll}
      />
    </>
  );
};

export default PaymentSearchSection;
